import React, { useState, useEffect } from "react";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import CommonFormActions from "../../components/common/CommonFormActions";
import CommonModal from "../../components/common/CommonModal";
import MainLayout from "../../components/layout/MainLayout";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Holidays.css";

function WeeklyHolidays() {
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [error, setError] = useState("");
  const [companyOwner, setCompanyOwner] = useState(false);

  const weekDays = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ];

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    try {
      const response = await api.get("/weekly-holidays");

      if (response.data.success) {
        setHolidays(response.data.data);
        setCompanyOwner(response.data.companyOwner);
      }
    } catch (error) {
      console.error("Failed to fetch weekly holidays:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load weekly holidays",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      } else {
        return [...prev, day];
      }
    });
    setError("");
  };

  const handleAddHoliday = () => {
    setSelectedDays([]);
    setError("");
    setShowAddModal(true);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();

    if (selectedDays.length === 0) {
      setError("Please select at least one day");
      return;
    }

    try {
      const response = await api.post("/weekly-holidays", {
        days: selectedDays,
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false,
        });

        setShowAddModal(false);
        setSelectedDays([]);
        fetchHolidays();
      }
    } catch (error) {
      console.error("Failed to create weekly holidays:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "Failed to create weekly holidays",
      });
    }
  };

  const handleDelete = async (holidayId, day) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Remove ${day} as weekly holiday?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6557ff",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.delete(`/weekly-holidays/${holidayId}`);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Weekly holiday deleted successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        fetchHolidays();
      }
    } catch (error) {
      console.error("Failed to delete weekly holiday:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "Failed to delete weekly holiday",
      });
    }
  };

  const canManageHolidays = () => {
    return companyOwner;
  };

  const activeCount = holidays.filter((holiday) => holiday.is_active === 1).length;
  const inactiveCount = holidays.filter((holiday) => holiday.is_active !== 1).length;

  if (loading) {
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </MainLayout>
    );
  }

  const holidayColumns = [
    {
      header: "Sr. No.",
      render: (_, index) => index + 1,
    },
    {
      header: "Days",
      render: (holiday) => <span className="day-badge">{holiday.day}</span>,
    },
    ...(canManageHolidays()
      ? [
          {
            header: "Created By",
            render: (holiday) => holiday.created_by_name || "N/A",
          },
          {
            header: "Active Status",
            render: (holiday) => (
              <span
                className={`status-pill ${holiday.is_active === 1 ? "active" : "inactive"}`}
              >
                {holiday.is_active === 1 ? "Active" : "Inactive"}
              </span>
            ),
          },
          {
            header: "Action",
            render: (holiday) => (
              <CommonActionMenu
                ariaLabel={`Actions for ${holiday.day}`}
                actions={[
                  {
                    label: "Delete",
                    icon: "fas fa-trash",
                    variant: "danger",
                    onClick: () => handleDelete(holiday.id, holiday.day),
                  },
                ]}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <MainLayout>
      <div className="weekly-holidays-container">
        <div className="holidays-card">
          <div className="card-header-custom">
            <div className="header-empty">
              <span className="holiday-guidance-label">Holiday Setup</span>
              <h3>Weekly Holidays</h3>
              <p className="weekly-holidays-subtitle">
                Define recurring weekly off days used across attendance and payroll working-day calculations.
              </p>
            </div>
            {canManageHolidays() && (
              <button className="btn-add-holiday" onClick={handleAddHoliday}>
                + Add Holiday
              </button>
            )}
          </div>

          <div className="holiday-guidance-banner">
            <div>
              <span className="holiday-guidance-label">Current Schedule</span>
              <strong>{holidays.length ? `${holidays.length} weekly holiday${holidays.length > 1 ? "s" : ""} configured` : "No weekly holidays configured yet"}</strong>
              <p>
                Keep this aligned with your real company weekend policy so attendance, leave, and payroll all stay in sync.
              </p>
            </div>
            <div className="holiday-target-chips">
              <span className="holiday-target-chip">Active {activeCount}</span>
              <span className="holiday-target-chip">Inactive {inactiveCount}</span>
              <span className="holiday-target-chip">Company scope</span>
            </div>
          </div>

          <div className="card-body-custom">
            <CommonTable
              columns={holidayColumns}
              data={holidays}
              tableClassName="holidays-table-clean"
              wrapperClassName="holidays-table-wrap"
              noDataClassName="text-center py-5 no-data"
              emptyMessage="No weekly holidays found"
            />
          </div>
        </div>

        {/* Add Holiday Modal */}
        {showAddModal && (
          <CommonModal
            onClose={() => setShowAddModal(false)}
            title="Add New Holiday"
            overlayClassName="modal-backdrop-custom"
            contentClassName="modal-custom"
            headerClassName="modal-header-custom"
            bodyClassName="modal-body-custom"
            footerClassName="modal-footer-custom"
            closeButtonClassName="close-modal-btn"
            footer={
              <CommonFormActions
                className="modal-footer-custom"
                cancelLabel="Close"
                submitLabel="Submit"
                onCancel={() => setShowAddModal(false)}
                onSubmit={handleSubmitAdd}
                submitType="button"
              />
            }
          >
              <form onSubmit={handleSubmitAdd}>
                <div>
                  <div className="form-row-checkbox">
                    <label>
                      Week Days<span className="required">*</span>
                    </label>
                    <div className="checkbox-group">
                      {weekDays.map((day) => (
                        <div key={day.value} className="checkbox-item">
                          <input
                            type="checkbox"
                            id={day.value}
                            value={day.value}
                            checked={selectedDays.includes(day.value)}
                            onChange={() => handleDayChange(day.value)}
                          />
                          <label htmlFor={day.value}>{day.label}</label>
                        </div>
                      ))}
                    </div>
                    {error && <span className="error-msg">{error}</span>}
                  </div>
                </div>
              </form>
          </CommonModal>
        )}
      </div>
    </MainLayout>
  );
}

export default WeeklyHolidays;
